/**
 * EditPackageModal Component
 *
 * Edit modal for package products.
 * Uses package domain hook + shared primitives.
 */

import { type JSX } from 'react';
import { CartItem } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import { getCartItemDisplayTitle } from '@repo/widget-utils/display-helper';
import { getDateRangeVariant } from 'src/widgets/activities/activity-booking/booking/Booking';
import { useBookingContext } from 'src/widgets/BookingContext';
import useSummaryPriceLines from 'src/hooks/domain/useSummaryPriceLines';
import { usePricePropsFromQuantitiesInBookingContext } from '../../booking-widget-card/booking-header/BookingHeader';
import BookingBase from '../../booking-base/BookingBase';
import { EditModalWrapper } from './primitives/EditModalWrapper';
import { useEditCartItem } from './primitives/useEditCartItem';
import { useEditModalState } from './primitives/useEditModalState';
import { usePackageQuantities } from 'src/hooks/domain/booking/usePackageQuantities';
import { showError } from 'src/utils/widget/error-handling';

export type EditPackageModalProps = {
    cartItem: CartItem;
    anchorEl: HTMLElement | null;
    onClose: () => void;
};

/**
 * Inner content component - uses BookingContext provided by EditModalWrapper
 */
function EditPackageModalContent(props: EditPackageModalProps): JSX.Element {
    const { cartItem, anchorEl, onClose } = props;
    const { t } = useLocale();
    const { hasChosenDate, isLoadingPrices, selectedTicketType } = useBookingContext();

    // 🟢 Reuse: Modal state management
    const modalState = useEditModalState({ cartItem, anchorEl });

    // 🟢 Reuse: Save/cancel logic
    const { save, cancel } = useEditCartItem({
        cartItem,
        onAfterSave: onClose,
    });

    // 🟢 Domain-specific: Package quantities (pass enriched quantities for proper validation)
    const { inputItems, errors, onChangeQuantity } = usePackageQuantities(
        modalState.priceQuantities,
    );

    // Derive UI data
    const productTitle = getCartItemDisplayTitle(cartItem);
    const numPrices = modalState.product?.ticketOptions.length;

    const dateRangeVariant = getDateRangeVariant(
        modalState.product ?? cartItem.products[0]?.product,
    );

    // Price summary
    const quantitiesForSummary = hasChosenDate ? modalState.priceQuantities : [];
    const summaryLines = useSummaryPriceLines(
        quantitiesForSummary,
        false, // isDetailedLines
        isLoadingPrices,
        selectedTicketType,
    );

    // Booking header price props
    const bookingHeaderPriceProps = usePricePropsFromQuantitiesInBookingContext(
        modalState.priceQuantities,
        undefined, // fromPrice
    );

    // Translate errors to warning label (simpler for packages)
    const warningLabel =
        errors.length > 0
            ? errors[0].error === 'minEntrants'
                ? t.a_minimum_of_participants_is_required_to_book_this_product
                : t.no_available_capacity_for_this_tour
            : undefined;

    // Error handling wrapper for save operation
    const handleSave = async () => {
        try {
            await save();
        } catch (_error) {
            showError(t.an_error_occurred_when_submitting_your_request);
        }
    };

    return (
        <BookingBase
            isPackageCalendar={true}
            disableFixedPosition={true}
            productTitle={productTitle}
            numPrices={numPrices}
            travelerQuantities={modalState.priceQuantities}
            availabilityData={modalState.availabilities}
            leftButtonLabel={capitalize(t.cancel)}
            rightButtonLabel={capitalize(t.update)}
            onClickLeftButton={cancel}
            onClickRightButton={handleSave}
            availabilitySearchPeriod={modalState.availabilitySearchPeriod}
            setAvailabilitySearchPeriod={modalState.setAvailabilitySearchPeriod}
            productCapacity={modalState.productCapacity}
            title={t.edit}
            addMarginRightToHeader={true}
            expandArrowInside={false}
            positionOffscreen={false}
            selectedDateRange={modalState.dateRange}
            onSelectDateRange={modalState.setDateRange}
            ticketTypes={modalState.ticketTypes}
            dateRangeVariant={dateRangeVariant}
            minDate={modalState.minBookingDate}
            isLoadingAvailabilities={modalState.isLoadingAvailabilities}
            summaryLines={summaryLines}
            quantityErrors={errors}
            totalErrors={errors}
            disableBookButton={errors.length > 0}
            warningLabel={warningLabel}
            quantityInputItems={inputItems}
            onChangeQuantity={onChangeQuantity}
            bookingHeaderPriceProps={bookingHeaderPriceProps}
        />
    );
}

/**
 * Package edit modal - provides BookingContext via EditModalWrapper
 */
export function EditPackageModal(props: EditPackageModalProps): JSX.Element {
    const { cartItem, anchorEl, onClose } = props;

    return (
        <EditModalWrapper cartItem={cartItem} anchorEl={anchorEl} onCancel={onClose}>
            <EditPackageModalContent {...props} />
        </EditModalWrapper>
    );
}
